/**
 * Typed calls for the real `/v2` Payouts API surface (the ~12 endpoints that exist today).
 * Every function takes a {@link RequestContext} ({ env, apiKey }) so the in-app environment
 * switcher decides which deployment is hit. The signed-in dashboard instead authenticates with the
 * user access token via {@link AuthContext} (see the "Dashboard" and "Webhooks" sections below).
 * Unbacked portal features (logs, events, cross-business payment/delivery lists, aggregates) have no
 * function here — see docs/payouts-portal-api-todo.md.
 *
 * Source of truth: src/apps/external/src/v2/payouts/payouts.api.external.yaml
 */
import { http, request, authRequest, ApiError, type RequestContext, type AuthContext } from './client';
import type { AddressSuggestion } from './lookup';
import type { Business, SignupRequest, SignupResponse } from './types';
import { getSonarSessionToken } from '@/lib/sonarSdk';

const sonarHeader = (sonarToken?: string): Record<string, string> => (sonarToken ? { 'Melio-Sonar-Token': sonarToken } : {});

/** Collections may come back as `[...]` or `{ data: [...] }`; tolerate both. */
type ListResponse<T> = T[] | { data: T[] };
const unwrap = <T>(res: ListResponse<T>): T[] => (Array.isArray(res) ? res : (res?.data ?? []));

/** The `me` sentinel resolves any `{businessId}` to the caller's sole business (self-serve). */
export const ME = 'me';

// ---- Signup (admin-gated) ---------------------------------------------------------------
export async function signupPartner(
  env: RequestContext['env'],
  adminToken: string,
  body: SignupRequest,
  signal?: AbortSignal
): Promise<SignupResponse> {
  return http<SignupResponse>(env, '/partners', {
    method: 'POST',
    body,
    headers: { 'x-admin-api-key': adminToken },
    signal,
  });
}

// ---- Entities (a "business" in the portal UI is an entity) ------------------------------
const entityHeader = (entityId: string): Record<string, string> => ({ 'Melio-Entity-Id': entityId });

export async function listBusinesses(ctx: RequestContext, signal?: AbortSignal): Promise<Business[]> {
  return unwrap(await request<ListResponse<Business>>(ctx, '/entities', { signal }));
}

export async function getBusiness(ctx: RequestContext, entityId: string, signal?: AbortSignal): Promise<Business> {
  return request<Business>(ctx, `/entities/${entityId}`, { signal });
}

export async function patchBusiness(
  ctx: RequestContext,
  entityId: string,
  patch: Record<string, unknown>,
  signal?: AbortSignal
): Promise<Business> {
  return request<Business>(ctx, `/entities/${entityId}`, { method: 'PATCH', body: patch, signal });
}

/** GET /v2/health — validates a partner api-key and returns the partner it belongs to. */
export async function getHealth(ctx: RequestContext, signal?: AbortSignal): Promise<{ partnerName?: string; status?: string }> {
  return request<{ partnerName?: string; status?: string }>(ctx, '/health', { signal });
}

/**
 * POST /v2/entities — create a business entity (the KYB onboarding call). Adds a fresh
 * Idempotency-Key per attempt and the risk (Sonar) token when one is available, mirroring
 * the simulator's create-business call.
 */
export async function createEntity(
  ctx: RequestContext,
  body: Record<string, unknown>,
  opts: { sonarToken?: string; signal?: AbortSignal } = {}
): Promise<Business & { id: string }> {
  return request<Business & { id: string }>(ctx, '/entities', {
    method: 'POST',
    headers: { 'Idempotency-Key': crypto.randomUUID(), ...sonarHeader(opts.sonarToken) },
    body,
    signal: opts.signal,
  });
}

export type Limitations = { canMakePayments: boolean; missingFields: string[]; blockingReasons: string[] };

/**
 * GET /v2/limitations (Melio-Entity-Id header) — the eligibility / "what's missing" surface.
 * Maps the server's per-capability shape onto the flat form the dashboard renders.
 */
export async function getLimitations(ctx: RequestContext, entityId: string, signal?: AbortSignal): Promise<Limitations> {
  const res = await request<{ canSendDomesticPayment?: { allowed?: boolean; missingFields?: string[] } }>(ctx, '/limitations', {
    headers: entityHeader(entityId),
    signal,
  });
  const cap = res?.canSendDomesticPayment;
  return { canMakePayments: !!cap?.allowed, missingFields: cap?.missingFields ?? [], blockingReasons: [] };
}

// ---- Dashboard (user access token) ------------------------------------------------------
// The signed-in dashboard authenticates payouts calls with the user's access token (Bearer) via
// {@link AuthContext} — no partner api-key in the browser. Entities, eligibility, and webhooks are
// managed through these.

/** GET /v2/entities — the partner's business entities (access-token auth). */
export async function listEntities(actx: AuthContext, signal?: AbortSignal): Promise<Business[]> {
  return unwrap(await authRequest<ListResponse<Business>>(actx, '/entities', { signal }));
}

/** GET /v2/entities/:id — a single business entity (access-token auth). Tolerates a `{ data }` wrapper. */
export async function getEntity(actx: AuthContext, entityId: string, signal?: AbortSignal): Promise<Business> {
  const res = await authRequest<Business | { data: Business }>(actx, `/entities/${entityId}`, { signal });
  return res && 'data' in res ? res.data : res;
}

/**
 * POST /v2/entities — create a business entity (access-token auth). The KYB create call onboarding and
 * the dashboard's "Add entity" make — payouts resolves the partner from the token, so the browser never
 * needs the api-key. Goes through {@link authRequest} like every other dashboard call, so a stale token
 * is refreshed and retried once rather than surfacing as a 401. Sonar token (minted from the non-secret
 * partnerName) rides along when present.
 */
export async function createEntityWithAuth(
  actx: AuthContext,
  body: Record<string, unknown>,
  opts: { sonarToken?: string; signal?: AbortSignal } = {}
): Promise<Business & { id: string }> {
  return authRequest<Business & { id: string }>(actx, '/entities', {
    method: 'POST',
    headers: { 'Idempotency-Key': crypto.randomUUID(), ...sonarHeader(opts.sonarToken) },
    body,
    signal: opts.signal,
  });
}

/** PATCH /v2/entities/:id — update a business entity (access-token auth). */
export async function patchEntity(
  actx: AuthContext,
  entityId: string,
  patch: Record<string, unknown>,
  signal?: AbortSignal
): Promise<Business> {
  return authRequest<Business>(actx, `/entities/${entityId}`, { method: 'PATCH', body: patch, signal });
}

/** GET /v2/limitations (Melio-Entity-Id header) — eligibility surface (access-token auth). */
export async function getEntityLimitations(actx: AuthContext, entityId: string, signal?: AbortSignal): Promise<Limitations> {
  const res = await authRequest<{ canSendDomesticPayment?: { allowed?: boolean; missingFields?: string[] } }>(actx, '/limitations', {
    headers: entityHeader(entityId),
    signal,
  });
  const cap = res?.canSendDomesticPayment;
  return { canMakePayments: !!cap?.allowed, missingFields: cap?.missingFields ?? [], blockingReasons: [] };
}

// ---- Funding sources = internal accounts ------------------------------------------------
export async function listInternalAccounts(ctx: RequestContext, entityId: string, signal?: AbortSignal): Promise<any[]> {
  return unwrap(await request<ListResponse<any>>(ctx, '/accounts?ownershipType=internal', { headers: entityHeader(entityId), signal }));
}

export async function createInternalAccount(
  ctx: RequestContext,
  entityId: string,
  body: Record<string, unknown>,
  signal?: AbortSignal
): Promise<any> {
  return request<any>(ctx, '/accounts', {
    method: 'POST',
    headers: entityHeader(entityId),
    body: { ownershipType: 'internal', ...body },
    signal,
  });
}

// ---- Delivery methods = external accounts (no dedicated list endpoint) ------------------
export async function createDeliveryMethod(
  ctx: RequestContext,
  entityId: string,
  body: Record<string, unknown>,
  signal?: AbortSignal
): Promise<any> {
  return request<any>(ctx, '/accounts', {
    method: 'POST',
    headers: entityHeader(entityId),
    body: { ownershipType: 'external', ...body },
    signal,
  });
}

export async function getDeliveryMethod(ctx: RequestContext, entityId: string, accountId: string, signal?: AbortSignal): Promise<any> {
  return request<any>(ctx, `/accounts/${accountId}`, { headers: entityHeader(entityId), signal });
}

// ---- Payments ---------------------------------------------------------------------------
export async function listPayments(ctx: RequestContext, entityId: string, signal?: AbortSignal): Promise<any[]> {
  return unwrap(await request<ListResponse<any>>(ctx, '/payments', { headers: entityHeader(entityId), signal }));
}

export async function createPayment(
  ctx: RequestContext,
  entityId: string,
  body: Record<string, unknown>,
  signal?: AbortSignal
): Promise<any> {
  // Payment creation requires an Idempotency-Key header (omitting it returns 400 IDEMPOTENCY_KEY_REQUIRED).
  return request<any>(ctx, '/payments', {
    method: 'POST',
    headers: { ...entityHeader(entityId), 'Idempotency-Key': crypto.randomUUID() },
    body,
    signal,
  });
}

export async function getPayment(ctx: RequestContext, entityId: string, paymentId: string, signal?: AbortSignal): Promise<any> {
  return request<any>(ctx, `/payments/${paymentId}`, { headers: entityHeader(entityId), signal });
}

// ---- Funding source links ---------------------------------------------------------------
export type InternalAccountLinkType = 'plaid' | 'card';

export type InternalAccountLink = {
  /** Portal URL the partner hands to the business. The scoped session JWT is embedded as `?token=`. */
  url: string;
  expiration: string;
};

/**
 * POST /v2/accounts/link (Melio-Entity-Id header) — mint a scoped link (partner api-key).
 * The business opens the returned `url`, which lands on the portal and runs the Plaid/card flow.
 */
export async function createInternalAccountLink(
  ctx: RequestContext,
  entityId: string,
  type: InternalAccountLinkType,
  signal?: AbortSignal
): Promise<InternalAccountLink> {
  const res = await request<{ data: InternalAccountLink }>(ctx, '/accounts/link', {
    method: 'POST',
    headers: { 'Melio-Entity-Id': entityId },
    body: { type },
    signal,
  });
  return res.data;
}

/**
 * POST /v2/accounts (Melio-Entity-Id header) authenticated with the scoped session token
 * (Bearer) rather than the partner api-key — used by the business-facing add-funding-source
 * flow, which never holds the partner api-key. A funding source is an `internal` account.
 */
export async function submitPlaidInternalAccountWithSession(
  env: RequestContext['env'],
  sessionToken: string,
  entityId: string,
  publishableKey: string,
  details: { plaidToken: string; plaidAccountId: string; nickname?: string },
  signal?: AbortSignal
): Promise<{ id: string }> {
  const { nickname, ...plaid } = details;
  const sonarToken = await getSonarSessionToken(env, publishableKey, entityId);
  return http<{ id: string }>(env, '/accounts', {
    method: 'POST',
    headers: { Authorization: `Bearer ${sessionToken}`, 'Melio-Entity-Id': entityId, ...sonarHeader(sonarToken) },
    body: { ownershipType: 'internal', type: 'plaid', ...(nickname ? { nickname } : {}), details: plaid },
    signal,
  });
}

/**
 * POST /v2/accounts/{accountId}/verify/link (Melio-Entity-Id header) — mint a scoped link (partner
 * api-key) that lets the business reopen Plaid in update mode to complete micro-deposit
 * verification for an existing unverified Plaid account.
 */
export async function createAccountVerifyLink(
  ctx: RequestContext,
  entityId: string,
  accountId: string,
  signal?: AbortSignal
): Promise<InternalAccountLink> {
  const res = await request<{ data: InternalAccountLink }>(ctx, `/accounts/${accountId}/verify/link`, {
    method: 'POST',
    headers: { 'Melio-Entity-Id': entityId },
    signal,
  });
  return res.data;
}

/**
 * GET /v2/lookup/address/suggestions — public address autocomplete (no entity scope).
 * Used by the business-facing card flow's address autocomplete (no partner api-key in the browser).
 */
export async function lookupAddressesWithSession(
  env: RequestContext['env'],
  sessionToken: string,
  entityId: string,
  q: string,
  signal?: AbortSignal
): Promise<AddressSuggestion[]> {
  const path = `/lookup/address/suggestions?q=${encodeURIComponent(q)}&type=domestic`;
  const res = await http<{ data: AddressSuggestion[] }>(env, path, {
    headers: { Authorization: `Bearer ${sessionToken}` },
    signal,
  });
  return res?.data ?? [];
}

export type CardInternalAccountDetails = {
  /** Vault token for the card (Basis Theory token id). */
  token: string;
  cardBin: string;
  expirationMonth: string;
  expirationYear: string;
  lastFourDigits: string;
  cardOwner: { firstName: string; lastName: string };
  address: { line1: string; line2?: string; city: string; state: string; postalCode: string };
};

/**
 * POST /v2/accounts (Melio-Entity-Id header) for a card, authenticated with the scoped session
 * token (Bearer). The PAN is tokenized client-side via Basis Theory — only the vault token reaches us.
 */
export async function submitCardInternalAccountWithSession(
  env: RequestContext['env'],
  sessionToken: string,
  entityId: string,
  publishableKey: string,
  details: CardInternalAccountDetails,
  signal?: AbortSignal
): Promise<{ id: string }> {
  const sonarToken = await getSonarSessionToken(env, publishableKey, entityId);
  return http<{ id: string }>(env, '/accounts', {
    method: 'POST',
    headers: { Authorization: `Bearer ${sessionToken}`, 'Melio-Entity-Id': entityId, ...sonarHeader(sonarToken) },
    body: { ownershipType: 'internal', type: 'card', details: { tokenProvider: 'basistheory', ...details } },
    signal,
  });
}
